import { firestore } from "firebase";
import {
  RunOverview,
  isIdleRun,
  isActiveRun,
  getInitialMeterReading,
  getAdjustedEndDate,
  getLatestRunEndMillis,
  wasRunInterrupted,
} from "./RunOverview";
import * as _ from "lodash";
import {
  hasOutdatedStatus,
  IresDevice,
  getIresDeviceCategoryFromSerialNumber,
  IresDeviceCategory,
} from "./IresDevice";
import {
  PanelSampleInfo,
  ComBoxSampleInfo,
  getFloorSampleData,
  getRoomSampleData,
  isPauseStartSample,
  isRunReachedMaxDurationSample,
  isPauseEndSample,
  isGeneratedPowerFailSample,
  isNightPauseStartSample,
  isDryingPauseStartSample,
  isDryingPauseEndSample,
  isNightPauseEndSample,
  hasV2OrGreaterSampleData,
  isUartStatusSample,
  isDryingPauseInfoSample,
  isConfigurationTypeInfoSample,
  isConfigurationInfoSample,
} from "./SampleInfo";
import {
  MinMaxKeyValueAggregator,
  SensorDataKeyValue,
} from "@/utils/SensorData";
import { ComBoxHumidityUnit, getHumidityValue } from "./ComBoxHumidityUnit";
import { MeterReadingInfo } from "../repository/PanelRepository";
import { ProjectAssignmentDetails } from "./ProjectAssignmentInfo";

import { formatDurationLong } from "@/utils/DateFormatter";
import { hasPlaceholderClockDate } from "@/utils/PanelClockPlaceholders";

import {
  addMinutes,
  advanceByMinutes,
  areOnSameDay,
  getNumberDaysBetween,
  getNumberMinutesBetween,
  toStartOfDay,
} from "@/utils/DateHelper";

import { ProgramChainQualityRating } from "./ProgramChainQualityRating";
import {
  isMonitorProgramOfComBox,
  isPostDryingProgram,
  isProgram0OfPanel,
  isProgram1To7OfPanel,
  isProgramNumberUndefined,
  isRoomDryingProgram,
  isRoomDryingProgramRevision0,
  isRoomDryingProgramRevision1,
  isTargetDryingProgram,
} from "./ComBox";
import { IresDeviceResponseReader } from "@/utils/IresDeviceResponseReader";
import { loadSamplesOfRun } from "../repository/RunRepository";
import { decideStoredEndDate } from "./RunEndDateCorrection";
import { hexToUint8Array } from "@/utils/ByteUtils";
import { getActiveUser } from "../repository/UserRepository";
import * as base64Buffer from "base64-arraybuffer";
import { IresDeviceAssignment } from "./IresDeviceAssignment";
import { markEndDateManualCorrectionsAccess } from "@/permissions/UserPermissions";

const OVERVIEW_PROGRAM_CHAIN_ID = "overview";
const NEW_DRY_RUN_PROGRAM_CHAIN_ID = "new_dry_run";

export interface ProgramChain {
  startDate: firestore.Timestamp;
  serialNumber: string;
  id: string;
  chainNumber: number;
  numNotes?: number;

  assignedClientId?: string;
  assignedProjectId?: string;
  assignedProjectDetails?: ProjectAssignmentDetails;
  assignmentId?: string;

  runs: Array<RunOverview>;
  rating?: ProgramChainQualityRating;
}

export enum ProgramChainTimeRegionOfInterestType {
  POWER_FAIL = "POWER_FAIL",
  DRYING_PAUSE = "DRYING_PAUSE",
  PHASE = "PHASE",
  POST_DRYING_RUN = "POST_DRYING_RUN",
}

export enum ProgramChainTimeRegionOfInterestTypeOfPanel {
  PROGRAM_0 = "PROGRAM_0",
  PROGRAM_1_TO_7 = "PROGRAM_1_TO_7",
  HAS_WARNING = "HAS_WARNING",
  HAS_ERROR = "HAS_ERROR",
}

export interface ProgramChainTimeRegionOfInterest {
  startDate: Date;
  endDate: Date;
  regionOfInterestType: ProgramChainTimeRegionOfInterestType;
  data?: DryingPhaseInfo;
  /**
   * A power cut between two runs only: when the run before it took its last reading. The cut starts
   * at `startDate`, the stored run end, which the box writes up to one sample interval after that
   * reading; the graph draws the band from the reading instead (see `getDrawnRunEndX`).
   */
  lastSampleDate?: Date;
}

export interface ProgramChainTimeRegionOfInterestOfPanel {
  startDate: Date;
  endDate: Date;
  regionOfInterestType: ProgramChainTimeRegionOfInterestTypeOfPanel;
  data?: DryingPhaseInfoOfPanel;
}

export interface DryingPhaseInfo {
  phase: number;
  phaseEndDay: number;
  phaseEndSample: ComBoxSampleInfo;

  phaseStartDate: Date;
  phaseEndDate: Date;

  isOpenEndedPhase?: boolean;
  formatPhasesType1?: boolean;
}

export interface DryingPhaseInfoOfPanel {
  phase: number;
  phaseEndDay: number;
  phaseEndSample: PanelSampleInfo;

  phaseStartDate: Date;
  phaseEndDate: Date;
}

export function isProgramChainAssignedTo(
  programChain: ProgramChain,
  assignmentId: string
) {
  if (programChain.assignedProjectDetails) {
    return programChain.assignedProjectDetails.assignmentId === assignmentId;
  }

  return programChain.runs.some(
    (run) => run.projectAssignmentDetails?.assignmentId === assignmentId
  );
}

export function doesProgramChainIncludeActiveRun(
  programChain: ProgramChain
): boolean {
  const lastRun = programChain.runs[programChain.runs.length - 1];
  return lastRun && isActiveRun(lastRun);
}

export function createNewDryRunProgramChain(
  serialNumber: string
): ProgramChain {
  const now = firestore.Timestamp.now();

  return {
    startDate: now,
    serialNumber: serialNumber,
    id: NEW_DRY_RUN_PROGRAM_CHAIN_ID,
    chainNumber: -1,
    runs: [],
  };
}

export function isNewDryRunProgramChain(programChain: ProgramChain) {
  return programChain.id == NEW_DRY_RUN_PROGRAM_CHAIN_ID;
}

export function getFloorSensorData(programChain: ProgramChain) {
  return programChain.runs.flatMap((run) =>
    run.samples.flatMap((sample) =>
      getFloorSampleData(sample as ComBoxSampleInfo)
    )
  );
}

export function getRoomSensorData(programChain: ProgramChain) {
  return programChain.runs.flatMap((run) =>
    run.samples.flatMap((sample) =>
      getRoomSampleData(sample as ComBoxSampleInfo)
    )
  );
}

export function getGuideSensorValuesOfComBoxRunOverview(
  runOverview: RunOverview,
  roomSensors: boolean,
  humidityUnit: ComBoxHumidityUnit
) {
  const temp = new MinMaxKeyValueAggregator(
    (value) => value !== undefined && value !== 255
  );
  const humidity = new MinMaxKeyValueAggregator(
    (value) => value !== undefined && value !== 255
  );

  runOverview.samples.forEach((sample) => {
    const comBoxSampleInfo = sample as ComBoxSampleInfo;

    const isUartStatus = isUartStatusSample(comBoxSampleInfo);
    const isDryingPauseInfo = isDryingPauseInfoSample(comBoxSampleInfo);
    const isConfigurationInfo = isConfigurationInfoSample(comBoxSampleInfo);
    const isConfigurationTypeInfo = isConfigurationTypeInfoSample(comBoxSampleInfo);

    const isSpecialEvent = 
      isUartStatus || 
      isDryingPauseInfo ||
      isConfigurationInfo ||
      isConfigurationTypeInfo;

    if (!isSpecialEvent) {
      if (roomSensors) {
        getRoomSampleData(comBoxSampleInfo).forEach((data) => {
          temp.addValue(data.temperature);
          humidity.addValue(getHumidityValue(humidityUnit, data));
        });
      } else if (isMonitorProgramOfComBox(runOverview.programNumber)) {
        getFloorSampleData(comBoxSampleInfo)
          .filter((data) => data.relativeHumidity !== undefined && data.relativeHumidity !== Number.MIN_SAFE_INTEGER)
          .forEach((data) => {
            temp.addValue(data.temperature);
            humidity.addValue(getHumidityValue(humidityUnit, data));
          });
      } else {
        getFloorSampleData(comBoxSampleInfo)
          .filter((data) => data.sensorIndex === comBoxSampleInfo.guideSensor)
          .forEach((data) => {
            temp.addValue(data.temperature);
            humidity.addValue(getHumidityValue(humidityUnit, data));
          });
      }
    }
  });

  return {
    temp: temp,
    humidity: humidity,
  };
}

export function getKeySensorValuesOfPanelRunOverview(runOverview: RunOverview) {
  const groundTempAggregator = new MinMaxKeyValueAggregator(
    (value) => value !== undefined && value !== 255
  );
  const wallTempAggregator = new MinMaxKeyValueAggregator(
    (value) => value !== undefined && value !== 255
  );
  const intensityAggregator = new MinMaxKeyValueAggregator(
    (value) => value !== undefined && value > 0 && value <= 100
  );

  runOverview.samples.forEach((sample) => {
    const sampleInfo = sample as PanelSampleInfo;

    const groundTempValue =
      sampleInfo.groundTemp !== undefined &&
      sampleInfo.maxGroundTempStep != undefined
        ? (sampleInfo.maxGroundTempStep + sampleInfo.groundTemp) / 2
        : undefined;

    groundTempAggregator.addMinMaxValue(
      sampleInfo.groundTemp,
      sampleInfo.maxGroundTempStep,
      groundTempValue
    );

    const wallTempValue =
      sampleInfo.wallTemp !== undefined &&
      sampleInfo.maxWallTempStep != undefined
        ? (sampleInfo.wallTemp + sampleInfo.maxWallTempStep) / 2
        : undefined;

    wallTempAggregator.addMinMaxValue(
      sampleInfo.wallTemp,
      sampleInfo.maxWallTempStep,
      wallTempValue
    );

    intensityAggregator.addValue(sampleInfo.heatRatioPercentage);
  });

  return {
    groundTemp: groundTempAggregator.toMinMaxKeyValue(SensorDataKeyValue.MAX),
    wallTemp: wallTempAggregator.toMinMaxKeyValue(SensorDataKeyValue.MAX),
    intensity: intensityAggregator.toMinMaxKeyValue(SensorDataKeyValue.MAX),
  };
}

export function getKeySensorValuesOfPanelProgramChain(
  programChain: ProgramChain
) {
  const groundTempAggregator = new MinMaxKeyValueAggregator(
    (value) => value !== undefined && value !== 255
  );
  const wallTempAggregator = new MinMaxKeyValueAggregator(
    (value) => value !== undefined && value !== 255
  );
  const intensityAggregator = new MinMaxKeyValueAggregator(
    (value) => value !== undefined && value > 0 && value <= 100
  );

  programChain.runs.forEach((run) => {
    const {
      groundTemp,
      wallTemp,
      intensity,
    } = getKeySensorValuesOfPanelRunOverview(run);

    groundTempAggregator.add(groundTemp);
    wallTempAggregator.add(wallTemp);
    intensityAggregator.add(intensity);
  });

  return {
    groundTemp: groundTempAggregator.toMinMaxKeyValue(SensorDataKeyValue.MAX),
    wallTemp: wallTempAggregator.toMinMaxKeyValue(SensorDataKeyValue.MAX),
    intensity: intensityAggregator.toMinMaxKeyValue(SensorDataKeyValue.MAX),
  };
}

export function getKeySensorDataOfComBoxRuns(
  runs: RunOverview[],
  roomSensors: boolean,
  humidityUnit: ComBoxHumidityUnit
) {
  const tempAggregator = new MinMaxKeyValueAggregator(
    (value) => value !== undefined && value !== 255
  );
  const humidityAggregator = new MinMaxKeyValueAggregator(
    (value) => value !== undefined && value !== 255
  );

  runs.forEach((run) => {
    const { temp, humidity } = getGuideSensorValuesOfComBoxRunOverview(
      run,
      roomSensors,
      humidityUnit
    );

    tempAggregator.add(temp);
    humidityAggregator.add(humidity);
  });

  return {
    temp: tempAggregator,
    humidity: humidityAggregator,
  };
}

/**
 * Aggregates historical temperature and humidity data for a specific sensor index across all runs.
 * Filters by sensor index in each sample's sensor data array, skipping special event samples.
 */
export function getPerSensorRunData(
  runs: RunOverview[],
  sensorIndex: number,
  humidityUnit: ComBoxHumidityUnit
) {
  const tempAggregator = new MinMaxKeyValueAggregator(
    (value) => value !== undefined && value !== 255
  );
  const humidityAggregator = new MinMaxKeyValueAggregator(
    (value) => value !== undefined && value !== 255
  );

  runs.forEach((run) => {
    run.samples.forEach((sample) => {
      const comBoxSampleInfo = sample as ComBoxSampleInfo;

      if (
        isUartStatusSample(comBoxSampleInfo) ||
        isDryingPauseInfoSample(comBoxSampleInfo) ||
        isConfigurationInfoSample(comBoxSampleInfo) ||
        isConfigurationTypeInfoSample(comBoxSampleInfo)
      ) {
        return;
      }

      const sensorData = comBoxSampleInfo.sensorData.find(
        (d) => d.sensorIndex === sensorIndex
      );

      if (
        sensorData &&
        sensorData.relativeHumidity !== undefined &&
        sensorData.relativeHumidity !== Number.MIN_SAFE_INTEGER
      ) {
        tempAggregator.addValue(sensorData.temperature);
        humidityAggregator.addValue(getHumidityValue(humidityUnit, sensorData));
      }
    });
  });

  return {
    temp: tempAggregator,
    humidity: humidityAggregator,
  };
}

export function matchesMinProgramChainDuration(
  programChain: ProgramChain,
  minProgramChainDuration?: number
) {
  if (minProgramChainDuration === 0) {
    return true;
  }

  if (!programChain) {
    return false;
  }

  if (!minProgramChainDuration) {
    return false;
  }

  let durationInMin = 0;
  let containsActiveRun = false;

  programChain.runs.forEach((run) => {
    /* We take the absolute value of the run duration here to address the 
       issue of faulty run end dates caused by low battery. */
    durationInMin += Math.abs(run.durationInMin);

    if (isActiveRun(run)) {
      containsActiveRun = true;
    }
  });

  return containsActiveRun || durationInMin >= minProgramChainDuration;
}

/**
 * Determines if all runs within a given program chain fall within the assignment periods of any device assignments.
 * This function iterates over each run in the `programChain` and checks if there is at least one device assignment
 * from `iresDeviceAssignments` that covers the run's period (from its start date to its end date). A run is considered
 * covered if its period is within the start and end dates of an assignment. If the assignment's end date is `null`,
 * it is treated as an ongoing assignment with no end date, extending to the maximum possible future date.
 *
 * @param {ProgramChain} programChain - An object representing the program chain, containing an array of `runs`.
 * Each `run` has a `startDate` and an `endDate`, both expected to be instances of a date or timestamp object with
 * a `seconds` property.
 *
 * @param {IresDeviceAssignment[]} iresDeviceAssignments - An array of device assignment objects. Each `assignment`
 * has a `startDate` and an optional `endDate`, both expected to be instances of a date or timestamp object with
 * a `seconds` property. If an `endDate` is `null`, the assignment is considered ongoing.
 *
 * @returns {boolean} Returns `true` if every run in the `programChain` falls within at least one of the provided
 * device assignments' periods, considering ongoing assignments as having no end date. Returns `false` otherwise,
 * including when the `programChain` is falsy.
 */
export function matchesDeviceAssignmentDetails(
  programChain: ProgramChain,
  iresDeviceAssignments: IresDeviceAssignment[],
) {
  if (!programChain) {
    return false;
  }

  let doesProgramChainBelongToCurrentClient = true;

  programChain.runs.forEach((run) => {
    let doesRunBelongToCurrentClient = false;

    iresDeviceAssignments.forEach((assignment) => {
      if (!doesRunBelongToCurrentClient) {
        // Check if the assignment's endDate is null. If so, consider it as an ongoing assignment without an end date.
        const assignmentEndDate = assignment.endDate ? assignment.endDate : new firestore.Timestamp(253402300799, 0); // This is the maximum possible Timestamp value (year 9999)
        doesRunBelongToCurrentClient = run.startDate.seconds >= assignment.startDate.seconds && run.endDate.seconds <= assignmentEndDate.seconds;
      }
    });

    if (doesProgramChainBelongToCurrentClient) {
      doesProgramChainBelongToCurrentClient = doesRunBelongToCurrentClient;
    }
  });

  return doesProgramChainBelongToCurrentClient;
}

export function filterRunsWithCorrectProgramNumber(
  programChain: ProgramChain
) {
  if (!programChain) {
    return false;
  }

  let shouldIncludeProgramChain = true;

  programChain.runs.forEach((run) => {
    if (isProgramNumberUndefined(run.programNumber)) {
      shouldIncludeProgramChain = false;
    }
  });

  return shouldIncludeProgramChain;
}

export function getMeterReadingInfo(programChain: ProgramChain) {
  if (!programChain) {
    return null;
  }

  const runs = programChain.runs;

  if (runs.length === 0) {
    return null;
  }

  let initialMeterReading = -1;
  let lastMeterReading = -1;

  runs.forEach((run) => {
    const initial = getInitialMeterReading(run);
    const latest = run.latestMeterReading;

    if (initialMeterReading === -1 && initial !== null) {
      initialMeterReading = initial;
    }

    if (latest !== undefined) {
      lastMeterReading = latest;
    }
  });

  if (initialMeterReading < 0 || lastMeterReading < 0) {
    return null;
  }

  return {
    initialMeterReading: initialMeterReading,
    lastMeterReading: lastMeterReading,
    totalPowerConsumed: Math.max(0, lastMeterReading - initialMeterReading),
  };
}

export function getHeatRatioOfMostRecentRun(programChain: ProgramChain) {
  const runs = programChain.runs;
  const percentage = runs[runs.length - 1].percentage;

  return percentage ? Math.floor(percentage) : 0;
}

export function getHeatRatio(programChain: ProgramChain) {
  let durationSum = 0;
  let heatRatioSum = 0;

  programChain.runs
    .filter((run) => !isIdleRun(run))
    .forEach((run) => {
      durationSum += run.durationInMin;
      heatRatioSum += Math.floor(run.percentage) * run.durationInMin;
    });

  return durationSum === 0 ? 0 : Math.floor(heatRatioSum / durationSum);
}

export function isOverviewProgramChain(programChain: ProgramChain) {
  return programChain.id === OVERVIEW_PROGRAM_CHAIN_ID;
}

export function createOverviewProgramChain(
  programChains: Array<ProgramChain>
): ProgramChain | null {
  if (programChains.length === 0) {
    return null;
  }

  const startDate = _.chain(programChains)
    .map((programChain) => programChain.startDate)
    .minBy((startDate) => startDate.seconds)
    .value();

  const runs = _.chain(programChains)
    .flatMap((programChain) => programChain.runs)
    .sortBy((runOverview) => runOverview.startDate.seconds)
    .value();

  const first = programChains[0];

  const firstProgramChainWithAssignmentDetails = programChains.find(
    (programChain) => !!programChain.assignedProjectId
  );

  return {
    startDate: startDate,
    id: OVERVIEW_PROGRAM_CHAIN_ID,
    serialNumber: first.serialNumber,
    assignedProjectId:
      firstProgramChainWithAssignmentDetails?.assignedProjectId,
    assignedProjectDetails:
      firstProgramChainWithAssignmentDetails?.assignedProjectDetails,
    chainNumber: 0,
    runs: runs,
  };
}

export function getDurationInMinutes(
  iresDevice: IresDevice,
  programChain: ProgramChain
) {
  if (hasOutdatedStatus(iresDevice)) {
    return -1;
  }

  const lastRun = programChain.runs[programChain.runs.length - 1];

  if (lastRun && isActiveRun(lastRun)) {
    return Math.floor(
      (new Date().getTime() - programChain.startDate.toDate().getTime()) /
        (1000 * 60)
    );
  }

  let durationInMin = 0;

  programChain.runs.forEach((run) => {
    durationInMin += run.durationInMin;
  });

  return durationInMin;
}

export function getEndDateOfProgramChain(programChain: ProgramChain) {
  const lastRun = programChain.runs[programChain.runs.length - 1];

  const iresDeviceCategory = getIresDeviceCategoryFromSerialNumber(
    programChain.serialNumber
  );

  if (iresDeviceCategory === IresDeviceCategory.PANEL) {
    return firestore.Timestamp.fromDate(getAdjustedEndDate(lastRun));
  } else {
    return lastRun.endDate;
  }
}

export function getMaxTemps(programChain: ProgramChain) {
  let maxWallTemp: undefined | number = undefined;
  let maxGroundTemp: undefined | number = undefined;

  programChain.runs.forEach((run) => {
    run.samples.forEach((sampleInfo) => {
      const sample = sampleInfo as PanelSampleInfo;

      if (
        sample.maxWallTempStep &&
        sample.maxWallTempStep !== 255 &&
        (!maxWallTemp || maxWallTemp < sample.maxWallTempStep)
      ) {
        maxWallTemp = sample.maxWallTempStep;
      }

      if (
        sample.maxGroundTempStep &&
        sample.maxGroundTempStep !== 255 &&
        (!maxGroundTemp || maxGroundTemp < sample.maxGroundTempStep)
      ) {
        maxGroundTemp = sample.maxGroundTempStep;
      }
    });
  });

  return {
    maxWallTemp: maxWallTemp,
    maxGroundTemp: maxGroundTemp,
  };
}

export function getLatestMeterReadingOfProgramChain(
  programChain: ProgramChain
): MeterReadingInfo | null {
  for (let i = 0; i < programChain.runs.length; i++) {
    const run = programChain.runs[programChain.runs.length - (i + 1)];

    const latestMeterReading = run.latestMeterReading;

    if (latestMeterReading !== undefined) {
      return {
        meterReadingInKWh: latestMeterReading,
        date: firestore.Timestamp.fromDate(getAdjustedEndDate(run)),
      };
    }
  }

  return null;
}

export function getInitialMeterReadingOfProgramChain(
  programChain: ProgramChain
): MeterReadingInfo | null {
  for (let i = 0; i < programChain.runs.length; i++) {
    const run = programChain.runs[i];

    const initialMeterReading = getInitialMeterReading(run);

    if (initialMeterReading !== null) {
      return {
        meterReadingInKWh: initialMeterReading,
        date: run.startDate,
      };
    }
  }

  return null;
}

function getPanelUnit16Value(iresDeviceResponseReader: IresDeviceResponseReader) {
  const uint16 =
    iresDeviceResponseReader.data[iresDeviceResponseReader.currentIndex] +
    iresDeviceResponseReader.data[iresDeviceResponseReader.currentIndex + 1] *
      256;

  iresDeviceResponseReader.skip(2);

  return uint16;
}

// To temporarily fix the issue with the battery management for the panels (HDxxx series)
async function computeNewEndDate(
  serialNumber: string,
  lastRun: RunOverview,
  programChainEndDate: firestore.Timestamp
) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const panelRunOverview = lastRun as any;
  let adjustedEndDate: Date | null = null;

  if (hasPlaceholderClockDate(panelRunOverview.endDate)) {
    const isPanel = getIresDeviceCategoryFromSerialNumber(serialNumber) === IresDeviceCategory.PANEL;
    const iresDeviceResponseReader = new IresDeviceResponseReader();

    const date = panelRunOverview.startDate.toDate();
    
    let previousTimeDiffInMin = 0;
    let timeDiffToBeAdjusted = 0;
    let didSkipLastSample = false;

    const samples = await loadSamplesOfRun(serialNumber, panelRunOverview.id);
    samples.forEach((rawSampleData) => {
      const data = isPanel
        ? hexToUint8Array(rawSampleData.encodedSample)
        : new Uint8Array(
            base64Buffer.decode(rawSampleData.encodedSample)
          );
      iresDeviceResponseReader.setNewData(data);

      if (!isPanel) {
        iresDeviceResponseReader.readUint16();
        iresDeviceResponseReader.readUint8();
        iresDeviceResponseReader.readUint8();

        if (hasV2OrGreaterSampleData(panelRunOverview)) {
          iresDeviceResponseReader.readUint8();
          iresDeviceResponseReader.readUint8();
          iresDeviceResponseReader.readUint8();
        }
      }

      const timeDiffInMinutes_ = isPanel
        ? getPanelUnit16Value(
            iresDeviceResponseReader
          )
        : iresDeviceResponseReader.readUint16();

      if (rawSampleData.sampleIndex === 0) { 
        timeDiffToBeAdjusted = timeDiffInMinutes_;
      }
      
      const timeDiffInMinutes = timeDiffInMinutes_ - timeDiffToBeAdjusted;

      if (previousTimeDiffInMin > timeDiffInMinutes) {
        didSkipLastSample = true;
      }
      else {
        advanceByMinutes(date, timeDiffInMinutes - previousTimeDiffInMin);

        if (rawSampleData.sampleIndex === panelRunOverview.lastLoadedSampleIndex) {
          adjustedEndDate = date as Date;
        }

        previousTimeDiffInMin = timeDiffInMinutes;
      }
    });

    if (didSkipLastSample) {
      adjustedEndDate = date as Date;
    }
  }

  return adjustedEndDate ?? programChainEndDate.toDate();
}

/**
 * The end date to show for a chain, and whether it came from a correction rather than the device.
 *
 * **One decision, made in one place.** The three callers below each used to repeat the same four
 * lines, and those four lines contained a real defect: `computeNewEndDate` decides whether to rebuild
 * from the LAST RUN's end date while the callers decide whether to use the result from the CHAIN's
 * end date. Those differ for an active Panel run, so a correction could be computed and discarded, or
 * the caller could "adjust" to exactly the placeholder it was trying to replace.
 *
 * **Prefers the value IRES Cloud stored.** Since the clock verdict is computed at write time
 * (`WriteRunEndDateCorrection`), a corrected run carries its rebuilt date and needs no sample load at
 * all. That is what makes Office, the Cloud PDF renderer and the Mira warehouse agree: they read one
 * stored answer instead of each recomputing their own.
 *
 * **A run recorded as known-wrong but NOT correctable keeps its raw date**, deliberately. The clock
 * drifted past the values we can explain, or the rebuild would have been anchored to a placeholder
 * start, or the samples carried no time at all. Showing the real, visibly broken date is the decided
 * behaviour: we do not silently correct what we cannot account for.
 *
 * The render-time rebuild remains as a fallback for runs the backfill has not reached yet.
 */
async function resolveProgramChainEndDate(
  programChain: ProgramChain
): Promise<{ endDate: Date; wasCorrected: boolean }> {
  const lastRun = programChain.runs[programChain.runs.length - 1];
  const rawChainEndDate = getEndDateOfProgramChain(programChain);

  const stored = decideStoredEndDate(lastRun);

  if (stored.source === "CORRECTION") {
    return { endDate: stored.endDate.toDate(), wasCorrected: true };
  }

  if (stored.source === "RAW_KNOWN_WRONG") {
    return { endDate: rawChainEndDate.toDate(), wasCorrected: false };
  }

  const needsEndDateAdjustment = hasPlaceholderClockDate(rawChainEndDate);
  const adjustedEndDate = await computeNewEndDate(
    programChain.serialNumber,
    lastRun,
    rawChainEndDate
  );

  return {
    endDate: needsEndDateAdjustment ? adjustedEndDate : rawChainEndDate.toDate(),
    wasCorrected: needsEndDateAdjustment,
  };
}

export async function getProgramChainDurationInMinutes(
  programChain: ProgramChain,
  openEnded?: boolean
) {
  const firstRun = programChain.runs[0];
  const lastRun = programChain.runs[programChain.runs.length - 1];
  const startDate = firstRun.startDate;

  const activeRun = isActiveRun(lastRun);

  const { endDate: programChainEndDate } = await resolveProgramChainEndDate(programChain);

  const endDate = !openEnded
    ? programChainEndDate
    : activeRun
    ? new Date()
    : programChainEndDate;

  return getNumberMinutesBetween(startDate.toDate(), endDate);
}

export async function getProgramChainEndDate(programChain: ProgramChain) {
  return (await resolveProgramChainEndDate(programChain)).endDate;
}

export async function getProgramChainDurationDescription(
  vue: Vue,
  programChain: ProgramChain,
  openEnded?: boolean
) {
  const firstRun = programChain.runs[0];
  const lastRun = programChain.runs[programChain.runs.length - 1];
  const startDate = firstRun.startDate;

  const activeRun = isActiveRun(lastRun);

  const { endDate: programChainEndDate, wasCorrected: needsEndDateAdjustment } =
    await resolveProgramChainEndDate(programChain);

  const endDate = !openEnded
    ? programChainEndDate
    : activeRun
    ? new Date()
    : programChainEndDate;

  let formattedDuration = "";

  if (areOnSameDay(startDate, firestore.Timestamp.fromDate(endDate))) {
    const numMinutes = getNumberMinutesBetween(startDate.toDate(), endDate);

    formattedDuration = formatDurationLong(
      vue,
      numMinutes,
      openEnded ?? false
    ).toString();
  } else {
    const numDays = getNumberDaysBetween(
      toStartOfDay(startDate).toDate(),
      endDate
    );

    formattedDuration = vue
      .$tc("common.duration.formatDays", numDays)
      .toString();
  }
  
  formattedDuration = markEndDateManualCorrectionsAccess(getActiveUser()) && needsEndDateAdjustment
    ? formattedDuration + "*"
    : formattedDuration
  ;

  return formattedDuration;
}

function getDryingPhaseInfoOfRunOfPanel(
  run: RunOverview,
  lastRunOfChain: RunOverview,
  startDate: Date
) {
  const result: DryingPhaseInfoOfPanel[] = [];
  let phaseStartDate = run.startDate.toDate();

  const filteredSamples = run.samples
    .map((sample) => sample as PanelSampleInfo);

  if (filteredSamples.length === 0) {
    return result;
  }
  const lastSample = filteredSamples[filteredSamples.length - 1];

  for (let i = 0; i < filteredSamples.length; i++) {
    const panelSampleInfo = filteredSamples[i] as PanelSampleInfo;
    const isLastSample =
      lastSample.sampleTimeInMinutes === panelSampleInfo.sampleTimeInMinutes;

    if (
      (run.id !== lastRunOfChain.id && isLastSample)
    ) {
      const sampleDate = addMinutes(
        run.startDate.toDate(),
        panelSampleInfo.sampleTimeInMinutes
      );

      const phaseEndDate =
        isActiveRun(run) && isLastSample ? new Date() : sampleDate;

      const dryingPhaseInfo: DryingPhaseInfoOfPanel = {
        phase: run.programNumber,

        phaseEndDay: getNumberDaysBetween(startDate, sampleDate, true),
        phaseEndSample: panelSampleInfo,

        phaseStartDate: phaseStartDate,
        phaseEndDate: phaseEndDate,
      };

      phaseStartDate = sampleDate;

      const previousInfo = result[result.length - 1];

      if (previousInfo?.phase === dryingPhaseInfo.phase) {
        result[result.length - 1] = {
          ...dryingPhaseInfo,
          phaseStartDate: previousInfo.phaseStartDate,
        };
      } else {
        result.push(dryingPhaseInfo);
      }
    }
  }

  return result;
}

function getDryingPhaseInfoOfRun(
  run: RunOverview,
  lastRunOfChain: RunOverview,
  includeOpenPhases: boolean,
  startDate: Date
) {
  //TODO: Change this to 0 for testing
  const isForRoomDryingProgram = isRoomDryingProgramRevision1(run.programNumber);
  const isForTargetDryingProgram = isTargetDryingProgram(run.programNumber);

  const result: DryingPhaseInfo[] = [];
  let phaseStartDate = run.startDate.toDate();

  const filteredSamples = run.samples
    .map((sample) => sample as ComBoxSampleInfo)
    .filter((sample) => 
      !isGeneratedPowerFailSample(sample) &&
      /**
        * There is a bug in firmware version 2.4.14 for the program ‘Room Drying’ revision 1, 
        * where Event 27 and Event 25 contain the wrong step information 
        * */
      (
        !isForRoomDryingProgram ||
        (
          sample.event !== 25 &&
          sample.event !== 27
        )
      )
    );

  if (filteredSamples.length === 0) {
    return result;
  }

  let activePhase = filteredSamples[0].step + 1;
  const activeRun = isActiveRun(run);
  const lastSample = filteredSamples[filteredSamples.length - 1];

  for (let i = 0; i < filteredSamples.length; i++) {
    const comBoxSampleInfo = filteredSamples[i] as ComBoxSampleInfo;
    const isLastSample =
      lastSample.sampleTimeInMinutes === comBoxSampleInfo.sampleTimeInMinutes;

    let phaseOfSample = comBoxSampleInfo.step + 1;
    const isEndOfStepSample = comBoxSampleInfo.event === 15;

    //TODO: Discuss and verify this
    if (isEndOfStepSample/*  && i !== 0 */ && !isForRoomDryingProgram) {
      const previousSample = filteredSamples[i - 1];

      //In firmware versions <= 1.19 the step of samples with event 15 referenced the step that was
      //just endend. This behaviour changed, in firmwares >= 1.1.9 it references the step that was
      //now started.
      phaseOfSample = (previousSample as ComBoxSampleInfo).step + 1;
    }

    if (
      phaseOfSample > activePhase ||
      isEndOfStepSample ||
      ((includeOpenPhases || run.id !== lastRunOfChain.id) && isLastSample)
    ) {
      const sampleDate = addMinutes(
        run.startDate.toDate(),
        comBoxSampleInfo.sampleTimeInMinutes
      );

      const phaseEndDate =
        isActiveRun(run) && isLastSample ? new Date() : sampleDate;

      const dryingPhaseInfo: DryingPhaseInfo = {
        phase: activePhase,

        phaseEndDay: getNumberDaysBetween(startDate, sampleDate, true),
        phaseEndSample: comBoxSampleInfo,

        phaseStartDate: phaseStartDate,
        phaseEndDate: phaseEndDate,

        isOpenEndedPhase: isLastSample && activeRun && !isEndOfStepSample,

        formatPhasesType1: isForRoomDryingProgram || isForTargetDryingProgram,
      };

      phaseStartDate = sampleDate;

      const previousInfo = result[result.length - 1];

      if (previousInfo?.phase === dryingPhaseInfo.phase) {
        result[result.length - 1] = {
          ...dryingPhaseInfo,
          phaseStartDate: previousInfo.phaseStartDate,
        };
      } else {
        result.push(dryingPhaseInfo);
      }

      activePhase = isLastSample
        ? dryingPhaseInfo.phase
        : isForRoomDryingProgram
          ? phaseOfSample
          : dryingPhaseInfo.phase + 1;
    } else {
      activePhase = phaseOfSample;
    }
  }

  return result;
}

export function getDryingPhaseInfoFromRunsOfPanel(
  runs: RunOverview[]
): DryingPhaseInfoOfPanel[] {
  const result: DryingPhaseInfoOfPanel[] = [];

  const filteredRuns = runs.filter(
    (run) =>
      !isProgram0OfPanel(run.programNumber) &&
      !isProgram1To7OfPanel(run.programNumber)
  );

  if (filteredRuns.length === 0) {
    return result;
  }

  const firstRun = filteredRuns[0];
  const lastRun = runs[runs.length - 1];

  const startDate = firstRun.startDate.toDate();

  result.push(
    ...getDryingPhaseInfoOfRunOfPanel(
      firstRun,
      lastRun,
      startDate
    )
  );

  filteredRuns.slice(1).forEach((run, index) => {
    const phaseDryingInfoOfRun = getDryingPhaseInfoOfRunOfPanel(
      run,
      lastRun,
      startDate
    );

    const firstNewInfo = phaseDryingInfoOfRun[0];

    if (firstNewInfo) {
      const previousInfo = result[result.length - 1];
      const previousRun = filteredRuns[index];

      const didProgramChainChange = previousRun.chainId != run.chainId;

      if (
        !didProgramChainChange &&
        previousInfo?.phase === firstNewInfo.phase
      ) {
        //Merge phase info that spans runs
        previousInfo.phaseEndDate = firstNewInfo.phaseEndDate;
        previousInfo.phaseEndSample = firstNewInfo.phaseEndSample;
        previousInfo.phaseEndDay = firstNewInfo.phaseEndDay;

        result.push(...phaseDryingInfoOfRun.slice(1));
      } else {
        result.push(...phaseDryingInfoOfRun);
      }
    }
  });

  return result;
}

export function getDryingPhaseInfoFromRuns(
  runs: RunOverview[],
  includeOpenPhases?: boolean,
  isForDryingInfoTable?: boolean,
): DryingPhaseInfo[] {
  const result: DryingPhaseInfo[] = [];

  const filteredRuns = runs.filter(
    (run) =>
      !isMonitorProgramOfComBox(run.programNumber) &&
      (
        isForDryingInfoTable
          ? !isRoomDryingProgram(run.programNumber) && !isTargetDryingProgram(run.programNumber)
            //TODO: Change this to 1 for testing
          : !isRoomDryingProgramRevision0(run.programNumber)
      ) &&
      !isPostDryingProgram(run.programNumber)
  );

  if (filteredRuns.length === 0) {
    return result;
  }

  const firstRun = filteredRuns[0];
  const lastRun = runs[runs.length - 1];

  const startDate = firstRun.startDate.toDate();

  result.push(
    ...getDryingPhaseInfoOfRun(
      firstRun,
      lastRun,
      includeOpenPhases ?? false,
      startDate
    )
  );

  filteredRuns.slice(1).forEach((run, index) => {
    const phaseDryingInfoOfRun = getDryingPhaseInfoOfRun(
      run,
      lastRun,
      includeOpenPhases ?? false,
      startDate
    );

    const firstNewInfo = phaseDryingInfoOfRun[0];

    if (firstNewInfo) {
      const previousInfo = result[result.length - 1];
      const previousRun = filteredRuns[index];

      const didProgramChainChange = previousRun.chainId != run.chainId;

      if (
        !didProgramChainChange &&
        previousInfo?.phase === firstNewInfo.phase
      ) {
        //Merge phase info that spans runs
        previousInfo.phaseEndDate = firstNewInfo.phaseEndDate;
        previousInfo.phaseEndSample = firstNewInfo.phaseEndSample;
        previousInfo.phaseEndDay = firstNewInfo.phaseEndDay;
        previousInfo.isOpenEndedPhase = firstNewInfo.isOpenEndedPhase;

        result.push(...phaseDryingInfoOfRun.slice(1));
      } else {
        result.push(...phaseDryingInfoOfRun);
      }
    }
  });

  return result;
}

export function getDryingPhaseInfoOfProgramChain(
  programChain: ProgramChain,
  includeOpenPhases?: boolean,
  isForDryingInfoTable?: boolean,
) {
  return getDryingPhaseInfoFromRuns(programChain.runs, includeOpenPhases, isForDryingInfoTable);
}

export function getProjectAssignmentDetailsOfProgramChain(
  programChain: ProgramChain,
  currentProjectAssignmentDetails: ProjectAssignmentDetails | null
) {
  if (programChain.assignedProjectDetails) {
    return programChain.assignedProjectDetails;
  }

  if (currentProjectAssignmentDetails) {
    const firstRunOfProgramChain = programChain.runs[0];

    if (
      firstRunOfProgramChain &&
      currentProjectAssignmentDetails.startDate.seconds <
        firstRunOfProgramChain.startDate.seconds
    ) {
      return currentProjectAssignmentDetails;
    }
  }

  return null;
}

/** When `run` took its last reading; `undefined` when it has none, or was loaded without samples. */
export function getLastSampleDate(run: RunOverview): Date | undefined {
  const samples = run.samples ?? [];
  const lastSample = samples[samples.length - 1];

  return lastSample
    ? addMinutes(run.startDate.toDate(), lastSample.sampleTimeInMinutes)
    : undefined;
}

async function findPowerFailRegionsOfInterest(
  serialNumber: string,
  runs: RunOverview[],
  iresDeviceCategory: IresDeviceCategory
): Promise<ProgramChainTimeRegionOfInterest[]> {
  const result: ProgramChainTimeRegionOfInterest[] = [];

  for (let index = 0; index < runs.length; index++) {
    const run = runs[index];
    const nextRun = runs[index + 1];

    if (nextRun && wasRunInterrupted(nextRun, runs.slice(0, index + 1))) {
      const runEndDate = hasPlaceholderClockDate(run.endDate)
        ? await computeNewEndDate(serialNumber, run, run.endDate)
        : run.endDate.toDate()
      ;

      // An earlier run can have ended after this one (see getLatestRunEndMillis): the cut starts at
      // the later end.
      result.push({
        startDate: new Date(
          Math.max(runEndDate.getTime(), getLatestRunEndMillis(runs.slice(0, index)))
        ),
        endDate: nextRun.startDate.toDate(),
        regionOfInterestType: ProgramChainTimeRegionOfInterestType.POWER_FAIL,
        lastSampleDate: getLastSampleDate(run),
      });
    } else if (!nextRun && iresDeviceCategory === IresDeviceCategory.COMBOX) {
      const lastSample = run.samples[
        run.samples.length - 1
      ] as ComBoxSampleInfo;

      if (lastSample?.event === 31 && isActiveRun(run)) {
        const powerFailDate = addMinutes(
          run.startDate.toDate(),
          lastSample.sampleTimeInMinutes
        );

        result.push({
          startDate: powerFailDate,
          endDate: new Date(),
          regionOfInterestType: ProgramChainTimeRegionOfInterestType.POWER_FAIL,
        });
      }
    }
  }

  return result;
}

async function findTimeRegionsOfInterestInPanelProgramChain(
  runs: RunOverview[],
  serialNumber: string
): Promise<ProgramChainTimeRegionOfInterestOfPanel[]> {
  if (runs.length === 0) {
    return [];
  }

  const result: ProgramChainTimeRegionOfInterestOfPanel[] = [];
  
  let startDate = runs[0].startDate.toDate();
  let endDate = isActiveRun(runs[0]) ? new Date() : runs[0].endDate.toDate();
  
  let lastProgramNumber = runs[0].programNumber;
  let isProgramChangeInteresting = false;

  let resetStartDate = false;

  for (let i = 0; i < runs.length; i++) {
    const run = runs[i];
    
    if (isProgram0OfPanel(lastProgramNumber) !== isProgram0OfPanel(run.programNumber)) {
      isProgramChangeInteresting = true;
    }

    if (isProgramChangeInteresting) {
      result.push({
        startDate: startDate,
        endDate: endDate,
        regionOfInterestType:
          isProgram0OfPanel(lastProgramNumber)
            ? ProgramChainTimeRegionOfInterestTypeOfPanel.PROGRAM_0
            : ProgramChainTimeRegionOfInterestTypeOfPanel.PROGRAM_1_TO_7,
      });

      resetStartDate = true;
      isProgramChangeInteresting = false;
    } 
    
    if (run === runs[runs.length - 1]) {
      result.push({
        startDate: resetStartDate ? run.startDate.toDate() : startDate,
        endDate: hasPlaceholderClockDate(run.endDate)
          ? await computeNewEndDate(serialNumber, run, run.endDate)
          : run.endDate.toDate(),
        regionOfInterestType:
          isProgram0OfPanel(run.programNumber)
            ? ProgramChainTimeRegionOfInterestTypeOfPanel.PROGRAM_0
            : ProgramChainTimeRegionOfInterestTypeOfPanel.PROGRAM_1_TO_7,
      });
    }

    if (resetStartDate) {
      resetStartDate = false;
      startDate = run.startDate.toDate();
    }

    endDate = run.endDate.toDate();

    if (hasPlaceholderClockDate(endDate)) {
      endDate = await computeNewEndDate(serialNumber, run, run.endDate);
    }

    lastProgramNumber = run.programNumber;
  }

  return result;
}

async function findTimeRegionsOfInterestInComBoxProgramChain(
  serialNumber: string,
  runs: RunOverview[]
): Promise<ProgramChainTimeRegionOfInterest[]> {
  if (runs.length === 0) {
    return [];
  }

  const result: ProgramChainTimeRegionOfInterest[] = [];

  let previousPauseStartTime: Date | null = null;
  let isNightPauseActive = false;
  let isDryingPauseActive = false;

  runs.forEach((run, runIndex) => {
    const samples = run.samples;

    let indexOfLastSampleWithSensorData = samples.length - 1;

    for (
      ;
      indexOfLastSampleWithSensorData >= 0;
      indexOfLastSampleWithSensorData--
    ) {
      const comBoxSampleInfo = samples[
        indexOfLastSampleWithSensorData
      ] as ComBoxSampleInfo;

      if (
        comBoxSampleInfo.sensorData.some(
          (sensorData) =>
            sensorData.temperature !== undefined ||
            sensorData.relativeHumidity !== undefined
        )
      ) {
        break;
      }
    }

    samples.forEach((sample, index) => {
      const comBoxSampleInfo = sample as ComBoxSampleInfo;

      const isPauseStart = isPauseStartSample(comBoxSampleInfo);
      const isPauseEnd = isPauseEndSample(comBoxSampleInfo);

      if (isNightPauseStartSample(comBoxSampleInfo) && !isNightPauseActive) {
        isNightPauseActive = true;
      } 

      if (isDryingPauseStartSample(comBoxSampleInfo) && !isDryingPauseActive) {
        isDryingPauseActive = true;
      }

      if (isNightPauseEndSample(comBoxSampleInfo) && isNightPauseActive) {
        isNightPauseActive = false;
      } 

      if (isDryingPauseEndSample(comBoxSampleInfo) && isDryingPauseActive) {
        isDryingPauseActive = false;
      }

      if (isPauseStart && previousPauseStartTime === null) {
        previousPauseStartTime = addMinutes(
          run.startDate.toDate(),
          sample.sampleTimeInMinutes
        );
      }

      let markPauseEndBecauseOfLastSampleReached =
        index === indexOfLastSampleWithSensorData;

      if (markPauseEndBecauseOfLastSampleReached && (!isPauseEnd || isNightPauseActive || isDryingPauseActive)) {
        const nextRun = runs[runIndex + 1];
        if (nextRun) {
          const firstSampleOfNextRun = nextRun.samples[0];

          if (
            firstSampleOfNextRun &&
            isRunReachedMaxDurationSample(
              firstSampleOfNextRun as ComBoxSampleInfo
            )
          ) {
            //When a run starts because the previous run was ended because of reaching the max run duration a pause that was started during the previous run will be continued in the newly started run.
            markPauseEndBecauseOfLastSampleReached = false;
          }
        }
      }

      if ((isPauseEnd && !isNightPauseActive && !isDryingPauseActive) || markPauseEndBecauseOfLastSampleReached) {
        if (previousPauseStartTime !== null) {
          const pauseEndDate = addMinutes(
            run.startDate.toDate(),
            index === indexOfLastSampleWithSensorData
              ? run.durationInMin
              : comBoxSampleInfo.sampleTimeInMinutes
          );

          result.push({
            startDate: previousPauseStartTime,
            endDate: pauseEndDate,
            regionOfInterestType:
              ProgramChainTimeRegionOfInterestType.DRYING_PAUSE,
          });
        }

        previousPauseStartTime = null;
      }
    });
  });

  const dryingPhaseInfo = getDryingPhaseInfoFromRuns(runs, true);
  const programOfFirstRun = runs[0].programNumber;

  //TODO: Change this to 1 for testing
  if (!isRoomDryingProgramRevision0(programOfFirstRun)) {
    //TODO: Phases for room drying should not be displayed in drying graph

    dryingPhaseInfo.forEach((info) => {
      result.push({
        startDate: info.phaseStartDate,
        endDate: info.phaseEndDate,
        regionOfInterestType: !info.formatPhasesType1 || info.phase !== 4
          ? ProgramChainTimeRegionOfInterestType.PHASE
          : ProgramChainTimeRegionOfInterestType.POST_DRYING_RUN,
        data: info,
      });
    });
  }

  runs
    .filter((run) => isPostDryingProgram(run.programNumber))
    .forEach((run) => {
      result.push({
        startDate: run.startDate.toDate(),
        endDate: isActiveRun(run) ? new Date() : run.endDate.toDate(),
        regionOfInterestType:
          ProgramChainTimeRegionOfInterestType.POST_DRYING_RUN,
      });
    });

  return [
    ...result,
    ... await findPowerFailRegionsOfInterest(serialNumber, runs, IresDeviceCategory.COMBOX),
  ];
}

export async function findTimeRegionsOfInterestInRuns(
  serialNumber: string,
  runs: RunOverview[]
) {
  const iresDeviceCategory = getIresDeviceCategoryFromSerialNumber(
    serialNumber
  );

  switch (iresDeviceCategory) {
    case IresDeviceCategory.COMBOX:
      return await findTimeRegionsOfInterestInComBoxProgramChain(serialNumber, runs);
    case IresDeviceCategory.PANEL:
      return await findPowerFailRegionsOfInterest(serialNumber, runs, iresDeviceCategory);
  }
}

export async function findTimeRegionsOfInterestInRunsOfPanel(runs: RunOverview[], serialNumber: string) {
  return await findTimeRegionsOfInterestInPanelProgramChain(runs, serialNumber);
}

export async function findTimeRegionsOfInterestInProgramChain(
  programChain: ProgramChain
): Promise<ProgramChainTimeRegionOfInterest[]> {
  return findTimeRegionsOfInterestInRuns(
    programChain.serialNumber,
    programChain.runs
  );
}
